import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COLORS } from '@gc-npm/utils.enums.colors';
import sendGaEvent from '@gc-npm/utils.metrics.send-ga-component-event';
import { actions as modalActions } from '@gc-npm/reducers.modals-reducer';
import TextInfo, {
  TEXT_KIND,
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_ALIGN,
} from '@gc-npm/components.text';
import CustomLink from '@gc-npm/components.custom-link';
import Button, {
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
} from '@gc-npm/components.button';
import Select from '@gc-npm/components.select';
import IconGlyph from '@gc-npm/components.icon-glyph';

import {
  actions as uiActions,
} from '@/reducers/ui';
import { API_STATUS, SCREEN_TYPES } from '@/utils/constants';
import BouncingLogo from '@/components/BouncingLogo';

const COMPONENT_NAME = 'C2CModal';

const getPhoneOptions = (phoneNumbers, misDatos) => {
  const phoneOptions = [];

  const { phone } = misDatos || {};

  if (phone) {
    phoneOptions.push({
      label: phone,
      value: phone,
    });
  }
  if (phoneNumbers && Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
    phoneNumbers.forEach((phoneNumber) => {
      phoneOptions.push({
        label: phoneNumber,
        value: phoneNumber,
      })
    });
  }

  // Eliminar numeros duplicados por "value"
  const uniquePhonesOptions = phoneOptions.filter(
    (item, index, self) => index === self.findIndex((t) => t.value === item.value),
  );

  return uniquePhonesOptions;
}

const C2CModal = ({
  data,
  onClick,
  screenType,
  componentProduct,
}) => {
  const {
    headerTitle,
    description,
    legalText,
    legalUrl,
    legalUrlText,
    acceptButton,
    legalConditions,
  } = data || {};
  const dispatch = useDispatch();

  const [selectedPhoneNumber, setSelectedPhoneNumberPhoneNumber] = useState(null);
  const [showLegalConditions, setShowLegalConditions] = useState(false);

  const client = useSelector((state) => state.client);

  const {
    misDatos, phoneNumbers, status,
  } = client || {};

  const phoneOptions = getPhoneOptions(phoneNumbers, misDatos);

  const taggingInfo = {
    componentName: COMPONENT_NAME,
    componentContent: '',
    componentProduct,
    componentVariation: 'all',
  };

  useEffect(() => {
    sendGaEvent({
      ...taggingInfo,
      eventType: 'print-success',
    });
  }, []);

  useEffect(() => {
    const phoneNumberSelect = document.querySelector('.C2CModal_Content-select');
    if (phoneNumberSelect) {
      phoneNumberSelect.style.height = `${(phoneOptions.length + 2) * 40}px`;
    }
  }, [phoneOptions]);

  const onClickHandler = () => {
    // siguiente prevention/purchaseProcess
    if (onClick) {
      onClick({
        kind: 'c2c',
        ...data,
        phone: selectedPhoneNumber,
        name: misDatos?.customerId,
      });
    }
  };

  const onCloseModal = () => {
    dispatch(
      modalActions.addModal({
        id: '',
      }),
    );
  };

  const onCancel = () => {
    // se arrepiente y no continua con el cambio
    onCloseModal();
  };

  const onChangeSelect = (value) => {
    setSelectedPhoneNumberPhoneNumber(value);
  }

  if (status === API_STATUS.KO) {
    if (misDatos === 401) {
      dispatch(uiActions.navigate2Login());
    } else {
      onClickHandler();
    }
  }

  return (
    <div className="C2CModal">
      {status !== API_STATUS.OK && (
        <BouncingLogo />
      )}
      {status === API_STATUS.OK && (
        <>
          <div className="C2CModal_Header">
            {headerTitle && (
            <TextInfo
              text={headerTitle}
              kind={TEXT_KIND.PARAGRAPH}
              color={TEXT_COLOR.MOVISTAR_BLACK}
              size={TEXT_SIZE.T7}
            />
            )}
            <div className="cerrar">
              <CustomLink
                taggingInfo={{ ...taggingInfo, componentContent: 'Cerrar' }}
                onClick={onCancel}
              >
                <IconGlyph
                  id="Cerrar"
                  height={14}
                  width={14}
                  color={TEXT_COLOR.MOVISTAR_BLUE}
                />
              </CustomLink>
            </div>
          </div>
          <hr />
          <div className="C2CModal_Content">
            {description && (
              <TextInfo
                text={description}
                kind={TEXT_KIND.PARAGRAPH}
                color={TEXT_COLOR.GREY6}
                size={TEXT_SIZE.BODY2}
              />
            )}
            {phoneOptions.length > 0 && (
              <div className="C2CModal_Content-select">
                <Select
                  options={phoneOptions}
                  defaultValue={phoneOptions[0].value}
                  onChange={onChangeSelect}
                  taggingInfo={taggingInfo}
                />
              </div>
            )}
            <div className="C2CModal_Content-legal">
              <div className="C2CModal_Content-legal_text">
                <TextInfo
                  kind={TEXT_KIND.PARAGRAPH}
                  text={legalText}
                  color={TEXT_COLOR.GREY6}
                  size={TEXT_SIZE.BODY2}
                  align={TEXT_ALIGN.CENTER}
                />
                <CustomLink
                  ariaLabel={legalUrlText}
                  href={legalUrl}
                  onClick={() => setShowLegalConditions(!showLegalConditions)}
                  taggingInfo={taggingInfo}
                  forceExternal
                  target="_blank"
                >
                  <TextInfo
                    kind={TEXT_KIND.PARAGRAPH}
                    text={legalUrlText}
                    color={TEXT_COLOR.MOVISTAR_BLUE}
                    size={TEXT_SIZE.BODY2}
                    align={TEXT_ALIGN.CENTER}
                  />
                </CustomLink>
              </div>
              <div className={`C2CModal_Content-legal_conditions ${showLegalConditions ? 'visible' : ''}`}>
                <TextInfo
                  kind={TEXT_KIND.PARAGRAPH}
                  text={legalConditions}
                  color={TEXT_COLOR.GREY6}
                  size={TEXT_SIZE.BODY2}
                />
              </div>
            </div>
          </div>
          <div className="C2CModal_Footer">
            <div className="C2CModal_Footer-buttons">
              {acceptButton && onClickHandler && (
                <Button
                  taggingInfo={{
                    ...taggingInfo,
                    componentContent: acceptButton,
                  }}
                  label={acceptButton}
                  title={acceptButton}
                  target="target"
                  ariaLabel={acceptButton}
                  onClick={onClickHandler}
                  height={BUTTON_HEIGHT.NORMAL}
                  width={screenType === SCREEN_TYPES.DESKTOP
                    ? BUTTON_WIDTH.MEDIUM
                    : BUTTON_WIDTH.FULL}
                />
              )}
            </div>
          </div>
        </>
      )}
      <style jsx>
        {`
          .C2CModal {
            background-color: ${COLORS.movistarWhite};
            padding: 1.5rem;
            border-radius: 16px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 1rem;
            width: 70vw;
            max-width: 808px;
            max-height: 80dvh;
            overflow: hidden;
            &_Header {
              display: flex;
              justify-content: space-between;
            }
            &_Content {
              display: flex;
              flex-direction: column;
              gap: 1rem;
              flex-grow: 1;
              overflow-y: auto !important;
              scrollbar-width: none;
              -ms-overflow-style: none;
              &-legal {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                &_text {
                  display: flex;
                  gap: 0.2rem;
                  flex-wrap: wrap;
                }
                &_conditions {
                  display: none;
                  &.visible {
                    display: block;
                  }
                }
              }
            }
            &_Footer {
              display: flex;
              flex-direction: column;
              gap: 1rem;
              &-buttons {
                display: flex;
                gap: 1rem;
              }
            }   
          }
          hr {
            width: 100%;
            color: ${COLORS.grey300};
          }
          @media (max-width: 768px) {
            .C2CModal {
              width: 100vw;
              border-radius: 1rem 1rem 0rem 0rem;
              max-height: 80dvh;
              &_Footer{
                &-buttons {
                  flex-direction: column;
                }
              }
            }
          }
        `}
      </style>
    </div>
  );
};

export default C2CModal;
